import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Dropdown } from 'antd'
import { Icon } from '@iconify/react'

import { getColumnBandClass, getCardColorStyle, getStoryMapFilterClass, isUntitledName, parseStepLabel } from '../utils'
import { CARD_HEIGHT, STEP_WIDTH } from '../constants'
import useCloseDropdownOnScroll from '../hooks/useCloseDropdownOnScroll'
import CardMetaIcons from './CardMetaIcons'
import styles from '../../ProjectStoryMapBoard.module.css'

const DRAG_CLICK_THRESHOLD = 5

const StepCard = ({
    step,
    boardTracks,
    trackIndex,
    dragProvided,
    isDragging,
    isEditing,
    editDraft,
    titleInputRef,
    onDraftChange,
    onCommitTitle,
    onOpen,
    stepMenu,
    isHighlighted = false,
    isFilterActive = false,
    isFilterMatchCurrent = false,
    readOnly = false,
}) => {
    const pointerStartRef = useRef(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const closeMenu = useCallback(() => setMenuOpen(false), [])
    useCloseDropdownOnScroll(menuOpen, closeMenu)
    const { version, name } = parseStepLabel(step.title)
    const bandClass = getColumnBandClass(boardTracks, trackIndex, styles)
    const { style: dragStyle, ...dragProps } = dragProvided?.draggableProps || {}
    const cardColorStyle = getCardColorStyle(step.color || step.accentColor)
    const filterClass = getStoryMapFilterClass(
        isFilterActive,
        step.matchesFilter,
        styles
    )

    const handlePointerDown = (event) => {
        pointerStartRef.current = { x: event.clientX, y: event.clientY }
    }

    const handleOpen = (event) => {
        // Draft steps exist only client-side — the detail drawer would 404.
        if (isDragging || isEditing || step.isDraft) return

        const start = pointerStartRef.current
        if (start) {
            const dx = Math.abs(event.clientX - start.x)
            const dy = Math.abs(event.clientY - start.y)
            if (dx > DRAG_CLICK_THRESHOLD || dy > DRAG_CLICK_THRESHOLD) {
                return
            }
        }

        onOpen?.(step.id)
    }

    const isFilterMatch =
        isFilterActive && step.matchesFilter === true

    const node = (
        <div
            ref={dragProvided?.innerRef}
            {...dragProps}
            className={`${styles.stepColumnCell} ${bandClass} ${
                isDragging ? styles.stepColumnCellDragging : ''
            }`}
            style={{
                ...dragStyle,
                ...(isDragging
                    ? {
                          zIndex: 10050,
                          width: STEP_WIDTH,
                          minWidth: STEP_WIDTH,
                          maxWidth: STEP_WIDTH,
                          height: CARD_HEIGHT,
                          minHeight: CARD_HEIGHT,
                          maxHeight: CARD_HEIGHT,
                          flex: `0 0 ${STEP_WIDTH}px`,
                          boxSizing: 'border-box',
                      }
                    : undefined),
            }}
        >
            <div
                className={styles.cardActionShell}
                {...(isFilterMatch ? { 'data-story-map-filter-match': 'true' } : {})}
            >
                <div
                    {...(!isEditing ? dragProvided?.dragHandleProps : {})}
                    className={`${styles.stepCard} ${
                        isDragging ? styles.stepCardDragging : ''
                    } ${isEditing ? styles.stepCardEditing : ''} ${
                        isHighlighted ? styles.stepCardHighlighted : ''
                    } ${step.isDraft ? styles.stepCardAgentDraft : ''} ${filterClass} ${
                        isFilterMatchCurrent ? styles.filterMatchFocused : ''
                    }`}
                    style={cardColorStyle}
                    data-story-map-step-id={step.id}
                >
                {!step.isDraft && (stepMenu?.(step.id) ?? []).length > 0 ? (
                    <Dropdown
                        menu={{ items: stepMenu(step.id) }}
                        trigger={['click']}
                        placement="bottomRight"
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        getPopupContainer={() => document.body}
                        destroyPopupOnHide
                    >
                        <button
                            type="button"
                            className={styles.stepMenuBtn}
                            aria-label="Step options"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Icon icon="mdi:dots-vertical" width={16} />
                        </button>
                    </Dropdown>
                ) : null}
                {isEditing ? (
                    <div className={styles.stepCardBody}>
                        <div className={styles.stepCardTop}>
                            <span className={styles.stepVersion}>{version}</span>
                        </div>
                        <input
                            ref={titleInputRef}
                            type="text"
                            className={styles.stepTitleInput}
                            value={editDraft}
                            placeholder="Untitled step"
                            aria-label="Step name"
                            onChange={(event) => onDraftChange(event.target.value)}
                            onBlur={(event) =>
                                onCommitTitle(step.id, event.target.value, {
                                    fromBlur: true,
                                })
                            }
                            onMouseDown={(event) => event.stopPropagation()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    onCommitTitle(step.id, event.currentTarget.value)
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div
                        className={styles.stepCardBody}
                        role="button"
                        tabIndex={0}
                        onPointerDown={handlePointerDown}
                        onClick={handleOpen}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                if (!isDragging) onOpen?.(step.id)
                            }
                        }}
                    >
                        <div className={styles.stepCardTop}>
                            <span className={styles.stepVersion}>{version}</span>
                            {step.isDraft ? (
                                <span className={styles.agentDraftBadge}>AI</span>
                            ) : null}
                            <CardMetaIcons item={step} />
                        </div>
                        <span
                            className={`${styles.stepTitle} ${
                                isUntitledName(name) ? styles.untitledLabel : ''
                            }`}
                        >
                            {name || 'Untitled step'}
                        </span>
                    </div>
                )}
                </div>
            </div>
        </div>
    )

    if (isDragging && typeof document !== 'undefined') {
        return createPortal(node, document.body)
    }

    return node
}

export default StepCard
